import { useState, useCallback } from "react";
import {
  Page,
  IndexTable,
  IndexFilters,
  Badge,
  Button,
  IndexFiltersMode,
  Card,
  Popover,
  ActionList,
  Loading,
  Link,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import {
  MenuHorizontalIcon,
  DuplicateIcon,
  EditIcon,
  DeleteIcon,
} from "@medentic/polaris-icons";
import type { IndexFiltersProps } from "@medentic/polaris-react";
import { AppPageWrapper } from "@shared/ui";
import { TabsTableSkeleton } from "@shared/ui";
import { CreateTagModal } from "@shared/ui";
import { RenameTagModal } from "@shared/ui";
import { Tag } from "@shared/ui";
import { AttendeeTag } from "@features/events/types";

export default function EventAttendeesTags() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET, POST, PUT, DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activePopoverId, setActivePopoverId] = useState<string | null>(null);
  const [tagToRename, setTagToRename] = useState<AttendeeTag | null>(null);
  const [tagToEdit, setTagToEdit] = useState<AttendeeTag | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Query for fetching tags
  const {
    data: tags = [],
    isLoading: isLoadingTags,
    isFetching: isFetchingTags,
  } = useQuery<AttendeeTag[]>({
    queryKey: ["attendee-tags", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/attendees/tags`),
    enabled: Boolean(hcc),
  });

  // Mutations
  const addTagMutation = useMutation({
    mutationFn: (tagData: Omit<AttendeeTag, "id">) =>
      POST(`/api/healthcare-companies/${hcc}/e/attendees/tags`, tagData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendee-tags", hcc] });
      setIsAddingTag(false);
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: (tagId: string) =>
      DELETE(`/api/healthcare-companies/${hcc}/e/attendees/tags/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendee-tags", hcc] });
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: ({
      tagId,
      tagData,
    }: {
      tagId: string;
      tagData: Omit<AttendeeTag, "id">;
    }) =>
      PUT(
        `/api/healthcare-companies/${hcc}/e/attendees/tags/${tagId}`,
        tagData
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendee-tags", hcc] });
      setTagToRename(null);
      setTagToEdit(null);
      setIsAddingTag(false);
    },
  });

  // Index Table setup
  const resourceName = {
    singular: t("tags.tag"),
    plural: t("tags.tags"),
  };

  const togglePopover = (id: string) => {
    setActivePopoverId(activePopoverId === id ? null : id);
  };

  const handleAction = (action: string, tagId: string) => {
    setActivePopoverId(null);
    const tag = tags.find((t) => t.id === tagId);
    if (!tag) return;

    switch (action) {
      case "duplicate":
        setTagToEdit({
          ...tag,
          name: `${tag.name} (copy)`,
          id: "", // Clear the ID since this will be a new tag
        });
        setIsDuplicating(true);
        setIsAddingTag(true);
        break;
      case "rename":
        setTagToRename(tag);
        break;
      case "delete":
        removeTagMutation.mutate(tagId);
        break;
    }
  };

  const handleRowClick = (tag: AttendeeTag) => {
    setTagToEdit(tag);
    setIsDuplicating(false);
    setIsAddingTag(true);
  };

  const handleTagSubmit = (tagData: Omit<AttendeeTag, "id">) => {
    if (isDuplicating) {
      addTagMutation.mutate(tagData);
    } else if (tagToEdit) {
      updateTagMutation.mutate({ tagId: tagToEdit.id, tagData });
    } else {
      addTagMutation.mutate(tagData);
    }
  };

  const handleModalClose = () => {
    setIsAddingTag(false);
    setTagToEdit(null);
    setIsDuplicating(false);
  };

  const rowMarkup = tags
    .filter((tag) =>
      (tag.name || "").toLowerCase().includes(searchValue.toLowerCase())
    )
    .map((tag, index) => (
      <IndexTable.Row
        id={tag.id}
        key={tag.id}
        position={index}
        onClick={() => handleRowClick(tag)}
      >
        <IndexTable.Cell>
          <Tag tagLabel={tag.name} tagId={tag.id} colorName={tag.color} />
        </IndexTable.Cell>
        <IndexTable.Cell>
          {tag.usageCount && tag.usageCount > 0 ? (
            <Link
              url={`/hcc/${hcc}/e/attendees?tags=${encodeURIComponent(tag.id)}`}
            >
              <Badge size="small" tone="info">
                {String(tag.usageCount)}
              </Badge>
            </Link>
          ) : (
            <Badge size="small">{String(tag.usageCount ?? 0)}</Badge>
          )}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <div
            style={{ display: "flex", justifyContent: "flex-end" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Popover
              active={activePopoverId === tag.id}
              activator={
                <div onClick={(e) => e.stopPropagation()}>
                  <Button
                    onClick={() => togglePopover(tag.id)}
                    variant="plain"
                    icon={MenuHorizontalIcon}
                  />
                </div>
              }
              onClose={() => setActivePopoverId(null)}
              preferredAlignment="right"
              preferredPosition="below"
            >
              <ActionList
                items={[
                  {
                    content: t("tags.duplicate"),
                    onAction: () => handleAction("duplicate", tag.id),
                    icon: DuplicateIcon,
                  },
                  {
                    content: t("tags.rename"),
                    onAction: () => handleAction("rename", tag.id),
                    icon: EditIcon,
                  },
                  {
                    content: t("tags.delete"),
                    onAction: () => handleAction("delete", tag.id),
                    icon: DeleteIcon,
                    destructive: true,
                  },
                ]}
              />
            </Popover>
          </div>
        </IndexTable.Cell>
      </IndexTable.Row>
    ));

  const handleFiltersQueryChange = useCallback(
    (value: string) => setSearchValue(value),
    []
  );

  const filters: IndexFiltersProps["filters"] = [];
  const appliedFilters: IndexFiltersProps["appliedFilters"] = [];

  return (
    <AppPageWrapper>
      <Page
        title={t("tags.title")}
        primaryAction={
          <Button onClick={() => setIsAddingTag(true)} variant="primary">
            {t("tags.createTag")}
          </Button>
        }
      >
        {isLoadingTags ? (
          <TabsTableSkeleton />
        ) : (
          <Card padding={"0"}>
            <IndexFilters
              queryValue={searchValue}
              queryPlaceholder={t("tags.searchPlaceholder")}
              onQueryChange={handleFiltersQueryChange}
              onQueryClear={() => setSearchValue("")}
              tabs={[]}
              selected={0}
              filters={filters}
              appliedFilters={appliedFilters}
              onClearAll={() => {}}
              mode={IndexFiltersMode.Filtering}
              setMode={() => {}}
            />

            <IndexTable
              resourceName={resourceName}
              itemCount={tags.length}
              selectable={false}
              headings={[
                { title: t("tags.name") },
                { title: t("tags.usageCount") },
                { title: "", alignment: "end" },
              ]}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        )}
        {isFetchingTags ||
          removeTagMutation.isPending ||
          (updateTagMutation.isPending && <Loading />)}

        <CreateTagModal
          open={isAddingTag}
          onClose={handleModalClose}
          onAddTag={handleTagSubmit}
          isSubmitting={addTagMutation.isPending || updateTagMutation.isPending}
          editTag={tagToEdit ?? undefined}
          isDuplicating={isDuplicating}
        />

        <RenameTagModal
          open={!!tagToRename}
          onClose={() => setTagToRename(null)}
          tag={tagToRename}
          onRename={(tagData) =>
            tagToRename &&
            updateTagMutation.mutate({ tagId: tagToRename.id, tagData })
          }
          isSubmitting={updateTagMutation.isPending}
        />
      </Page>
    </AppPageWrapper>
  );
}
